"use client";

import { useEffect, useState, useRef } from "react";
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  ChevronUp,
  ChevronDown,
  Layers,
  Copy,
  Minus,
  Plus,
  AlignCenterHorizontal,
  AlignCenterVertical,
  Maximize,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Slider } from "@/components/ui/slider";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

// Web-safe fonts
export const FONT_FAMILIES = [
  { value: "Arial", label: "Arial" },
  { value: "Helvetica", label: "Helvetica" },
  { value: "Verdana", label: "Verdana" },
  { value: "Tahoma", label: "Tahoma" },
  { value: "Georgia", label: "Georgia" },
  { value: "Times New Roman", label: "Times New Roman" },
  { value: "Palatino Linotype", label: "Palatino" },
  { value: "Courier New", label: "Courier New" },
  { value: "Monaco", label: "Monaco" },
  { value: "Impact", label: "Impact" },
  { value: "Comic Sans MS", label: "Comic Sans" },
];

// Canvas dimensions (slide size)
const CANVAS_WIDTH = 960;
const CANVAS_HEIGHT = 540;

// Preset colors for quick selection
const PRESET_COLORS = [
  "#000000", "#333333", "#666666", "#999999", "#ffffff",
  "#FFCC00", "#00B2A9", "#0066B3", "#003366", "#E31937",
  "#FF6B35", "#7CB518", "#9B5DE5", "#F15BB5", "#00BBF9",
];

interface TextProperties {
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: string;
  fontStyle?: string;
  textAlign?: string;
  underline?: boolean;
  linethrough?: boolean;
  fill?: string;
  backgroundColor?: string;
  opacity?: number;
}

interface ContextualToolbarProps {
  selectedElement: any | null;
  canvasContainerRef: React.RefObject<HTMLDivElement>;
  onUpdate: (properties: Record<string, unknown>) => void;
  onBringForward: () => void;
  onSendBackward: () => void;
  onBringToFront: () => void;
  onSendToBack: () => void;
  onDuplicate: () => void;
}

export function ContextualToolbar({
  selectedElement,
  canvasContainerRef,
  onUpdate,
  onBringForward,
  onSendBackward,
  onBringToFront,
  onSendToBack,
  onDuplicate,
}: ContextualToolbarProps) {
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isVisible, setIsVisible] = useState(false);

  // Check if multiple elements are selected (ActiveSelection)
  const isMultiSelection = selectedElement?.type === "activeselection";

  // Check if element is a text element (only for single selection)
  const isTextElement = selectedElement && !isMultiSelection &&
    (selectedElement.type === "textbox" || 
     selectedElement.type === "i-text" || 
     selectedElement.customType === "output_field");

  // Calculate position based on selected element
  useEffect(() => {
    if (!selectedElement || !canvasContainerRef.current) {
      setIsVisible(false);
      return;
    }

    // Store reference to the current element for cleanup
    const currentElement = selectedElement;

    const updatePosition = () => {
      const container = canvasContainerRef.current;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();

      // Get element bounding rect from Fabric.js
      const elemBounds = currentElement.getBoundingRect?.();
      if (!elemBounds) {
        // Fallback for elements without getBoundingRect
        setPosition({
          top: 10,
          left: containerRect.width / 2,
        });
        setIsVisible(true);
        return;
      }

      // Find the canvas element to get its offset within the container
      const canvasElement = container.querySelector("canvas");
      if (!canvasElement) {
        setIsVisible(false);
        return;
      }

      const canvasRect = canvasElement.getBoundingClientRect();
      
      // Calculate canvas offset relative to container
      const canvasOffsetLeft = canvasRect.left - containerRect.left;
      const canvasOffsetTop = canvasRect.top - containerRect.top;

      // Position toolbar above the element
      const toolbarHeight = 48;
      const toolbarWidth = toolbarRef.current?.offsetWidth || 500;
      
      // Adjust element bounds by canvas offset within container
      let top = canvasOffsetTop + elemBounds.top - toolbarHeight - 10;
      let left = canvasOffsetLeft + elemBounds.left + elemBounds.width / 2 - toolbarWidth / 2;

      // Keep within container bounds
      if (top < 10) {
        top = canvasOffsetTop + elemBounds.top + elemBounds.height + 10;
      }
      if (left < 10) {
        left = 10;
      }
      if (left + toolbarWidth > containerRect.width - 10) {
        left = containerRect.width - toolbarWidth - 10;
      }

      setPosition({ top, left });
      setIsVisible(true);
    };

    updatePosition();
    
    // Update position when element moves
    const handleModified = () => updatePosition();
    currentElement.on?.("modified", handleModified);
    currentElement.on?.("moving", handleModified);
    currentElement.on?.("scaling", handleModified);

    return () => {
      // Use the captured reference for cleanup to avoid stale closure issues
      currentElement.off?.("modified", handleModified);
      currentElement.off?.("moving", handleModified);
      currentElement.off?.("scaling", handleModified);
    };
  }, [selectedElement, canvasContainerRef]);

  if (!selectedElement || !isVisible) {
    return null;
  }

  // Get current properties from element
  const props: TextProperties = {
    fontFamily: selectedElement.fontFamily || "Arial",
    fontSize: selectedElement.fontSize || 16,
    fontWeight: selectedElement.fontWeight || "normal",
    fontStyle: selectedElement.fontStyle || "normal",
    textAlign: selectedElement.textAlign || "left",
    underline: selectedElement.underline || false,
    linethrough: selectedElement.linethrough || false,
    fill: selectedElement.fill || "#000000",
    backgroundColor: selectedElement.backgroundColor || "transparent",
    opacity: (selectedElement.opacity ?? 1) * 100,
  };

  const handleFontSizeChange = (delta: number) => {
    const newSize = Math.max(8, Math.min(200, (props.fontSize || 16) + delta));
    onUpdate({ fontSize: newSize });
  };

  const handleToggleBold = () => {
    onUpdate({ fontWeight: props.fontWeight === "bold" ? "normal" : "bold" });
  };

  const handleToggleItalic = () => {
    onUpdate({ fontStyle: props.fontStyle === "italic" ? "normal" : "italic" });
  };

  const handleToggleUnderline = () => {
    onUpdate({ underline: !props.underline });
  };

  const handleToggleLinethrough = () => {
    onUpdate({ linethrough: !props.linethrough });
  };

  return (
    <div
      ref={toolbarRef}
      className="absolute z-50 flex items-center gap-1 p-1.5 bg-background border rounded-lg shadow-lg"
      style={{
        top: position.top,
        left: position.left,
        transform: "translateX(0)",
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Font Family - Only for text elements */}
      {isTextElement && (
        <>
          <Select
            value={props.fontFamily}
            onValueChange={(value) => onUpdate({ fontFamily: value })}
          >
            <SelectTrigger className="w-32 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FONT_FAMILIES.map((font) => (
                <SelectItem
                  key={font.value}
                  value={font.value}
                  style={{ fontFamily: font.value }}
                >
                  {font.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Separator orientation="vertical" className="h-6 mx-1" />

          {/* Font Size */}
          <div className="flex items-center gap-0.5">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => handleFontSizeChange(-1)}
            >
              <Minus className="h-3 w-3" />
            </Button>
            <Input
              type="number"
              value={props.fontSize}
              onChange={(e) => onUpdate({ fontSize: parseInt(e.target.value) || 16 })}
              className="w-14 h-8 text-center text-xs px-1"
              min={8}
              max={200}
            />
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => handleFontSizeChange(1)}
            >
              <Plus className="h-3 w-3" />
            </Button>
          </div>

          <Separator orientation="vertical" className="h-6 mx-1" />

          {/* Text Styles */}
          <div className="flex items-center gap-0.5">
            <Button
              variant="ghost"
              size="icon"
              className={cn("h-8 w-8", props.fontWeight === "bold" && "bg-muted")}
              onClick={handleToggleBold}
              title="Gras (Ctrl+B)"
            >
              <Bold className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={cn("h-8 w-8", props.fontStyle === "italic" && "bg-muted")}
              onClick={handleToggleItalic}
              title="Italique (Ctrl+I)"
            >
              <Italic className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={cn("h-8 w-8", props.underline && "bg-muted")}
              onClick={handleToggleUnderline}
              title="Souligné (Ctrl+U)"
            >
              <Underline className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={cn("h-8 w-8", props.linethrough && "bg-muted")}
              onClick={handleToggleLinethrough}
              title="Barré"
            >
              <Strikethrough className="h-4 w-4" />
            </Button>
          </div>

          <Separator orientation="vertical" className="h-6 mx-1" />

          {/* Text Alignment */}
          <div className="flex items-center gap-0.5">
            <Button
              variant="ghost"
              size="icon"
              className={cn("h-8 w-8", props.textAlign === "left" && "bg-muted")}
              onClick={() => onUpdate({ textAlign: "left" })}
              title="Aligner à gauche"
            >
              <AlignLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={cn("h-8 w-8", props.textAlign === "center" && "bg-muted")}
              onClick={() => onUpdate({ textAlign: "center" })}
              title="Centrer"
            >
              <AlignCenter className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={cn("h-8 w-8", props.textAlign === "right" && "bg-muted")}
              onClick={() => onUpdate({ textAlign: "right" })}
              title="Aligner à droite"
            >
              <AlignRight className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={cn("h-8 w-8", props.textAlign === "justify" && "bg-muted")}
              onClick={() => onUpdate({ textAlign: "justify" })}
              title="Justifier"
            >
              <AlignJustify className="h-4 w-4" />
            </Button>
          </div>

          <Separator orientation="vertical" className="h-6 mx-1" />
        </>
      )}

      {/* Text Color */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 relative"
            title="Couleur du texte"
          >
            <span className="text-sm font-bold">A</span>
            <div
              className="absolute bottom-1 left-1 right-1 h-1 rounded"
              style={{ backgroundColor: props.fill }}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3">
          <div className="space-y-3">
            <div className="text-sm font-medium">Couleur du texte</div>
            <div className="grid grid-cols-5 gap-1.5">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color}
                  className={cn(
                    "w-7 h-7 rounded border hover:scale-110 transition-transform",
                    props.fill === color && "ring-2 ring-primary ring-offset-2"
                  )}
                  style={{ backgroundColor: color }}
                  onClick={() => onUpdate({ fill: color })}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                type="color"
                value={props.fill}
                onChange={(e) => onUpdate({ fill: e.target.value })}
                className="w-10 h-8 p-0 border-0 cursor-pointer"
              />
              <Input
                value={props.fill}
                onChange={(e) => onUpdate({ fill: e.target.value })}
                className="flex-1 h-8 text-xs"
                placeholder="#000000"
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Background Color - Only for text elements */}
      {isTextElement && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 relative"
              title="Couleur de fond"
            >
              <div
                className="w-5 h-5 rounded border"
                style={{ 
                  backgroundColor: props.backgroundColor === "transparent" ? "#ffffff" : props.backgroundColor,
                  backgroundImage: props.backgroundColor === "transparent" 
                    ? "linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)"
                    : "none",
                  backgroundSize: "8px 8px",
                  backgroundPosition: "0 0, 0 4px, 4px -4px, -4px 0px",
                }}
              />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-3">
            <div className="space-y-3">
              <div className="text-sm font-medium">Couleur de fond</div>
              <div className="grid grid-cols-5 gap-1.5">
                <button
                  className={cn(
                    "w-7 h-7 rounded border hover:scale-110 transition-transform",
                    props.backgroundColor === "transparent" && "ring-2 ring-primary ring-offset-2"
                  )}
                  style={{
                    backgroundImage: "linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)",
                    backgroundSize: "8px 8px",
                    backgroundPosition: "0 0, 0 4px, 4px -4px, -4px 0px",
                  }}
                  onClick={() => onUpdate({ backgroundColor: "transparent" })}
                  title="Transparent"
                />
                {PRESET_COLORS.slice(0, 4).map((color) => (
                  <button
                    key={color}
                    className={cn(
                      "w-7 h-7 rounded border hover:scale-110 transition-transform",
                      props.backgroundColor === color && "ring-2 ring-primary ring-offset-2"
                    )}
                    style={{ backgroundColor: color }}
                    onClick={() => onUpdate({ backgroundColor: color })}
                  />
                ))}
              </div>
              <div className="grid grid-cols-5 gap-1.5">
                {PRESET_COLORS.slice(4, 9).map((color) => (
                  <button
                    key={color}
                    className={cn(
                      "w-7 h-7 rounded border hover:scale-110 transition-transform",
                      props.backgroundColor === color && "ring-2 ring-primary ring-offset-2"
                    )}
                    style={{ backgroundColor: color }}
                    onClick={() => onUpdate({ backgroundColor: color })}
                  />
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={props.backgroundColor === "transparent" ? "#ffffff" : props.backgroundColor}
                  onChange={(e) => onUpdate({ backgroundColor: e.target.value })}
                  className="w-10 h-8 p-0 border-0 cursor-pointer"
                />
                <Input
                  value={props.backgroundColor}
                  onChange={(e) => onUpdate({ backgroundColor: e.target.value })}
                  className="flex-1 h-8 text-xs"
                  placeholder="transparent"
                />
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Center on slide */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8" title="Centrer sur le slide">
            <Maximize className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => {
            const elemWidth = (selectedElement.width || 0) * (selectedElement.scaleX || 1);
            const originX = selectedElement.originX || "left";
            const centerX = originX === "center"
              ? CANVAS_WIDTH / 2
              : (CANVAS_WIDTH - elemWidth) / 2;
            onUpdate({ left: centerX });
          }}>
            <AlignCenterHorizontal className="h-4 w-4 mr-2" />
            Centrer horizontalement
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => {
            const elemHeight = (selectedElement.height || 0) * (selectedElement.scaleY || 1);
            const originY = selectedElement.originY || "top";
            const centerY = originY === "center"
              ? CANVAS_HEIGHT / 2
              : (CANVAS_HEIGHT - elemHeight) / 2;
            onUpdate({ top: centerY });
          }}>
            <AlignCenterVertical className="h-4 w-4 mr-2" />
            Centrer verticalement
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => {
            const elemWidth = (selectedElement.width || 0) * (selectedElement.scaleX || 1);
            const elemHeight = (selectedElement.height || 0) * (selectedElement.scaleY || 1);
            const originX = selectedElement.originX || "left";
            const originY = selectedElement.originY || "top";
            const centerX = originX === "center"
              ? CANVAS_WIDTH / 2
              : (CANVAS_WIDTH - elemWidth) / 2;
            const centerY = originY === "center"
              ? CANVAS_HEIGHT / 2
              : (CANVAS_HEIGHT - elemHeight) / 2;
            onUpdate({ left: centerX, top: centerY });
          }}>
            <Maximize className="h-4 w-4 mr-2" />
            Centrer au milieu du slide
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Opacity */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2 text-xs gap-1"
            title="Opacité"
          >
            {Math.round(props.opacity || 100)}%
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-3">
          <div className="space-y-3">
            <div className="text-sm font-medium">Opacité</div>
            <Slider
              value={[props.opacity || 100]}
              onValueChange={([value]) => onUpdate({ opacity: value / 100 })}
              min={0}
              max={100}
              step={1}
            />
            <div className="text-center text-sm text-muted-foreground">
              {Math.round(props.opacity || 100)}%
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Z-ordering */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8" title="Organisation">
            <Layers className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onBringToFront}>
            <ChevronUp className="h-4 w-4 mr-2" />
            <ChevronUp className="h-4 w-4 mr-2 -ml-4" />
            Premier plan
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onBringForward}>
            <ChevronUp className="h-4 w-4 mr-2" />
            Avancer
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onSendBackward}>
            <ChevronDown className="h-4 w-4 mr-2" />
            Reculer
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onSendToBack}>
            <ChevronDown className="h-4 w-4 mr-2" />
            <ChevronDown className="h-4 w-4 mr-2 -ml-4" />
            Arrière-plan
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onDuplicate}>
            <Copy className="h-4 w-4 mr-2" />
            Dupliquer (Ctrl+D)
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
